import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send, MapPinned, Building2, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { cities } from '../mock';
import { servicesData } from '../servicesData';
import { submitEnquiry } from '../api';

export function ContactPage() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', mobile: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) {
      toast({ title: 'Missing details', description: 'Please enter your name and mobile.' });
      return;
    }
    setSubmitting(true);
    try {
      await submitEnquiry({ ...form, source: 'contact_page' });
      toast({ title: 'Message sent!', description: `Thanks ${form.name}, we'll get back within 24 hours.` });
      setForm({ name: '', email: '', mobile: '', subject: '', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again or call us directly.' });
    } finally {
      setSubmitting(false);
    }
  };

  const infoCards = [
    { icon: Mail, title: 'Email Us', lines: ['info@registerpoint.com', 'support@registerpoint.com'], accent: 'from-blue-500 to-blue-600' },
    { icon: MapPin, title: 'Visit Us', lines: ['Laxmi Nagar, Delhi', '110092, India'], accent: 'from-green-500 to-green-600' },
    { icon: Clock, title: 'Working Hours', lines: ['Mon–Sat 10:00–19:00', 'Sunday: Closed'], accent: 'from-purple-500 to-purple-600' },
  ];

  return (
    <div>
      <section className="bg-gradient-to-br from-[#f4f9fd] via-white to-[#fff4ec] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-14 md:py-20 text-center">
          <div className="text-xs font-bold uppercase tracking-widest text-orange-600">Contact Register Point</div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-[#0b3d5b] mt-3">We&apos;d love to hear from you</h1>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">Have a question about services, pricing or your compliance? Our team responds within 24 hours — usually much faster.</p>
        </div>
      </section>

      <section className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {infoCards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-lg transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.accent} flex items-center justify-center text-white shadow`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="font-bold text-[#0b3d5b] mt-4">{c.title}</div>
                {c.lines.map((l) => (<div key={l} className="text-sm text-slate-600">{l}</div>))}
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-slate-50/60 py-14">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-5 gap-8">
          <div className="md:col-span-2 bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
            <h2 className="text-2xl font-bold text-[#0b3d5b]">Send us a message</h2>
            <p className="text-sm text-slate-500 mt-1">Fill the form — our expert will call you back.</p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <input type="text" placeholder="Full Name*" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm" />
              <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm" />
              <input type="tel" placeholder="Mobile*" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm" />
              <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm bg-white text-slate-700">
                <option value="">Select Subject</option>
                {Object.values(servicesData).map((s) => (<option key={s.title}>{s.title}</option>))}
                <option>Other</option>
              </select>
              <textarea placeholder="Your message" rows="4" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm resize-none" />
              <button type="submit" disabled={submitting} className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-70 text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"><Send className="w-4 h-4" /> {submitting ? 'Sending…' : 'Send Message'}</button>
            </form>
          </div>
          <div className="md:col-span-3 space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center gap-2 text-[#0b3d5b] font-bold"><MapPinned className="w-5 h-5 text-orange-500" /> Our Location</div>
              <div className="mt-4 aspect-[16/8] rounded-xl overflow-hidden border border-slate-100">
                <iframe title="map" src="https://www.openstreetmap.org/export/embed.html?bbox=77.26%2C28.62%2C77.30%2C28.66&amp;layer=mapnik&amp;marker=28.6383%2C77.2773" className="w-full h-full" />
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <div className="flex items-center gap-2 text-[#0b3d5b] font-bold"><Building2 className="w-5 h-5 text-orange-500" /> Serving these cities</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {cities.map((c) => (
                  <Link key={c.name} to={`/city/${c.name.toLowerCase()}`} className="inline-flex items-center gap-1 bg-slate-50 hover:bg-orange-50 border border-slate-200 hover:border-orange-200 px-3 py-1.5 rounded-full text-sm text-slate-700 transition-colors">
                    <MapPin className="w-3.5 h-3.5 text-orange-500" /> {c.name}
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}

export function CityPage() {
  const { city } = useParams();
  const cityName = city ? city.charAt(0).toUpperCase() + city.slice(1) : 'India';

  return (
    <div>
      <section className="bg-gradient-to-br from-[#f4f9fd] via-white to-[#fff4ec] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-14 md:py-16">
          <nav className="text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-orange-600">Home</Link> <span className="mx-1.5">/</span> <span className="text-slate-700">CA Services in {cityName}</span>
          </nav>
          <div className="inline-flex items-center gap-2 bg-white border border-orange-200 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
            <MapPin className="w-4 h-4" /> Serving 500+ businesses in {cityName}
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-[#0b3d5b] leading-tight">Best Chartered Accountants in {cityName}</h1>
          <p className="mt-4 text-slate-600 max-w-3xl">Register Point connects you with verified CAs, CS and tax experts in {cityName}. From GST to company registration, ITR to trademark — every compliance solved online.</p>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0b3d5b]">Popular Services in {cityName}</h2>
          <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(servicesData).map(([slug, s]) => (
              <Link key={slug} to={`/services/${slug}`} className="group bg-white rounded-xl border border-slate-100 p-5 hover:shadow-lg hover:border-orange-200 transition-all">
                <div className="font-semibold text-[#0b3d5b] group-hover:text-orange-600">{s.title}</div>
                <div className="mt-1 text-xs text-slate-500">Starting {s.price}</div>
                <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-orange-600">Book in {cityName} <span>&rarr;</span></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50/60 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0b3d5b]">Why choose Register Point in {cityName}?</h2>
          <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-5">
            {[
              `Local, verified CAs based in ${cityName}`,
              'Transparent, fixed pricing — no hidden charges',
              'End-to-end online process, no office visits needed',
              'Same-day response, dedicated point of contact',
              'PAN-India expertise, local presence',
              'Post-service compliance reminders',
            ].map((f) => (
              <div key={f} className="flex items-start gap-2 bg-white p-4 rounded-xl border border-slate-100">
                <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">{f}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/contact" className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors">Talk to a CA in {cityName}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
